import React, {useEffect, useState, useRef} from 'react';
import {useParams} from 'react-router-dom';
import PuffLoader from 'react-spinners/PuffLoader';

const t: Record<string, Record<string, string>> = {
    en: {
        title: 'Report a problem on this site',
        categoryLabel: 'Category',
        descriptionLabel: 'Describe the issue (Optional)',
        pictureLabel: 'Upload picture (Optional)',
        uploadPhotos: 'Upload Picture',
        takePhoto: 'Take Photo',
        send: 'Send',
        thankYouTitle: 'Service Report',
        thankYouBody:
            'Issue reported. Thank you for helping us maintain the facility.\n\nOur team will review the report and address the problem as soon as possible.',
        rateLimitError:
            'You have reached the daily limit for how many reports can be submitted. Please try again tomorrow.',
        selectCategoryError: 'Category is required',
        minChars: 'Minimum 8 characters',
        maxChars: 'Maximum 512 characters',
        notFoundTitle: 'Site not found',
        notFoundBody: 'Service reporting is not enabled for this site.',
        genericError: 'Something went wrong. Please try again.',
    },
    nl: {
        title: 'Meld een probleem op deze locatie',
        categoryLabel: 'Categorie',
        descriptionLabel: 'Beschrijf het probleem (optioneel)',
        pictureLabel: 'Foto uploaden (optioneel)',
        uploadPhotos: 'Foto uploaden',
        takePhoto: 'Foto maken',
        send: 'Verzenden',
        thankYouTitle: 'Servicerapport',
        thankYouBody:
            'Probleem gemeld. Bedankt voor uw hulp bij het onderhoud van de faciliteit.\n\nOns team zal de melding bekijken en het probleem zo snel mogelijk oplossen.',
        rateLimitError:
            'U heeft de dagelijkse limiet bereikt voor het aantal rapporten dat kan worden ingediend. Probeer het morgen opnieuw.',
        selectCategoryError: 'Categorie is vereist',
        minChars: 'Minimaal 8 tekens',
        maxChars: 'Maximaal 512 tekens',
        notFoundTitle: 'Site niet gevonden',
        notFoundBody: 'Service-rapportage is niet ingeschakeld voor deze site.',
        genericError: 'Er is iets misgegaan. Probeer het opnieuw.',
    },
};

interface ImageItem {
    file: File;
    preview: string;
    key: string;
    uploading: boolean;
    uploaded: boolean;
}

const CameraIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{flexShrink: 0}}
    >
        <path
            d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5Z"
            fill="white"
        />
        <path
            d="M9 2L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4H16.83L15 2H9ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17Z"
            fill="white"
        />
    </svg>
);

const CameraIcon2 = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{flexShrink: 0}}
    >
        <path
            d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5Z"
            fill="#ff6701"
        />
        <path
            d="M9 2L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4H16.83L15 2H9ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17Z"
            fill="#ff6701"
        />
    </svg>
);

const GalleryIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{flexShrink: 0}}
    >
        <path
            d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
            fill="#ff6701"
        />
    </svg>
);

function ServiceReport() {
    const {siteId} = useParams<{siteId: string}>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const hasFetchedRef = useRef(false);

    const isAndroid = /android/i.test(navigator.userAgent);

    const [screen, setScreen] = useState<
        'loading' | 'form' | 'confirmation' | 'error' | 'notfound'
    >('loading');
    const [siteInfo, setSiteInfo] = useState<any>(null);
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState<ImageItem[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [rateLimitMessage, setRateLimitMessage] = useState('');
    const [lang, setLang] = useState('en');
    const [validationErrors, setValidationErrors] = useState<
        Record<string, string>
    >({});
    const [showUploadSheet, setShowUploadSheet] = useState(false);

    const detectedLang = navigator.language?.startsWith('nl') ? 'nl' : 'en';

    useEffect(() => {
        setLang(detectedLang);
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchSiteInfo();
        }
    }, []);

    const fetchSiteInfo = async () => {
        try {
            const response = await fetch(
                `https://api.kompan.fitness/service/${siteId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': detectedLang,
                    },
                }
            );

            if (response.status === 404) {
                setScreen('notfound');
                return;
            }

            if (response.ok) {
                const data = await response.json();
                setSiteInfo(data.site || data);
                setCategories(data.categories || []);
                setScreen('form');
            } else {
                setScreen('error');
                setErrorMessage(t[detectedLang].genericError);
            }
        } catch (err) {
            console.error('Error fetching site info:', err);
            setScreen('error');
            setErrorMessage(t[detectedLang].genericError);
        }
    };

    const handleImageSelect = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = e.target.files;
        if (!files) return;

        const remaining = 10 - images.length;
        const filesToAdd = Array.from(files).slice(0, remaining);

        for (const file of filesToAdd) {
            const preview = URL.createObjectURL(file);
            const newImage: ImageItem = {
                file,
                preview,
                key: '',
                uploading: true,
                uploaded: false,
            };

            setImages((prev) => [...prev, newImage]);

            try {
                // Read file into memory immediately — on Android the file URI
                // can become invalid after async operations (ERR_UPLOAD_FILE_CHANGED)
                const fileData = await file.arrayBuffer();

                const uploadRes = await fetch(
                    `https://api.kompan.fitness/service/${siteId}/upload`,
                    {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({
                            fileName: file.name,
                            fileType: file.type,
                        }),
                    }
                );

                if (!uploadRes.ok) {
                    setImages((prev) =>
                        prev.filter((img) => img.preview !== preview)
                    );
                    URL.revokeObjectURL(preview);
                    continue;
                }

                const {signedUrl, key} = await uploadRes.json();

                // fetch only rejects on network or CORS failure, so a 403 from
                // an expired presign (Expires: 300) or a 400 RequestTimeout from
                // a stalled mobile upload resolves normally. Without this check
                // the image is marked uploaded despite never reaching S3, and
                // the failure only surfaces as a generic error at submit time.
                const putRes = await fetch(signedUrl, {
                    method: 'PUT',
                    headers: {'Content-Type': file.type},
                    body: fileData,
                });

                if (!putRes.ok) {
                    setImages((prev) =>
                        prev.filter((img) => img.preview !== preview)
                    );
                    URL.revokeObjectURL(preview);
                    continue;
                }

                setImages((prev) =>
                    prev.map((img) =>
                        img.preview === preview
                            ? {...img, key, uploading: false, uploaded: true}
                            : img
                    )
                );
            } catch (err) {
                console.error('Error uploading image:', err);
                setImages((prev) =>
                    prev.filter((img) => img.preview !== preview)
                );
                URL.revokeObjectURL(preview);
            }
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeImage = (preview: string) => {
        setImages((prev) => {
            const img = prev.find((i) => i.preview === preview);
            if (img) URL.revokeObjectURL(img.preview);
            return prev.filter((i) => i.preview !== preview);
        });
    };

    const validate = (): boolean => {
        const errors: Record<string, string> = {};

        if (!selectedCategory) {
            errors.category = t[lang].selectCategoryError;
        }

        // The description is optional, but the API rejects 1-7 characters
        // (kompan-api service.ts: "description must be between 8 and 512
        // characters"). Matching that bound here is what keeps the feedback
        // inline and in red under the field, instead of the user submitting
        // and landing on the full-page error screen.
        if (description.length > 0 && description.length < 8) {
            errors.description = t[lang].minChars;
        }

        if (description.length > 512) {
            errors.description = t[lang].maxChars;
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setSubmitting(true);
        setValidationErrors({});
        setRateLimitMessage('');

        const fileKeys = images
            .filter((img) => img.uploaded)
            .map((img) => img.key);

        try {
            const response = await fetch(
                `https://api.kompan.fitness/service/${siteId}/report`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        serviceCategoryId: selectedCategory,
                        description: description || undefined,
                        fileKeys,
                        language: lang,
                    }),
                }
            );

            if (response.status === 429) {
                setRateLimitMessage(t[lang].rateLimitError);
                return;
            }

            if (response.ok) {
                setScreen('confirmation');
                return;
            }

            // The API returns specific, actionable messages ("No valid uploaded
            // images found", "description must be between 8 and 512 characters").
            // Replacing all of them with the generic string is what made this
            // arrive as an unactionable bug report.
            let message = t[lang].genericError;
            try {
                const body = await response.json();
                if (typeof body?.error === 'string' && body.error) {
                    message = body.error;
                }
            } catch {
                // No JSON body to read — fall back to the generic message.
            }

            setScreen('error');
            setErrorMessage(message);
        } catch (err) {
            console.error('Error submitting report:', err);
            setScreen('error');
            setErrorMessage(t[lang].genericError);
        } finally {
            setSubmitting(false);
        }
    };

    const renderHeader = () => (
        <div style={styles.header}>
            <img src="/kompan-logo.svg" alt="KOMPAN" style={styles.logo} />
        </div>
    );

    const renderFooter = () => (
        <div style={styles.footer}>
            <span style={styles.footerText}>
                Copyright &copy; KOMPAN A/S. All Rights Reserved
            </span>
        </div>
    );

    if (screen === 'loading') {
        return (
            <div style={styles.page}>
                {renderHeader()}
                <div style={styles.container}>
                    <div style={styles.loaderWrap}>
                        <PuffLoader
                            color={'#ff6701'}
                            loading={true}
                            size={84}
                            aria-label="Loading Spinner"
                        />
                    </div>
                </div>
                {renderFooter()}
            </div>
        );
    }

    if (screen === 'notfound') {
        return (
            <div style={styles.page}>
                {renderHeader()}
                <div style={styles.container}>
                    <div style={styles.notFoundContent}>
                        <p style={{...styles.title, textAlign: 'center'}}>
                            {t[lang].notFoundTitle}
                        </p>
                        <p style={{...styles.bodyText, textAlign: 'center'}}>
                            {t[lang].notFoundBody}
                        </p>
                    </div>
                </div>
                {renderFooter()}
            </div>
        );
    }

    if (screen === 'error') {
        return (
            <div style={styles.page}>
                {renderHeader()}
                <div style={styles.container}>
                    <p style={styles.title}>{t[lang].title}</p>
                    <p style={styles.bodyText}>{errorMessage}</p>
                </div>
                {renderFooter()}
            </div>
        );
    }

    if (screen === 'confirmation') {
        return (
            <div style={styles.page}>
                {renderHeader()}
                <div style={styles.container}>
                    <p style={{...styles.title, textAlign: 'center'}}>
                        {t[lang].thankYouTitle}
                    </p>
                    <p
                        style={{
                            ...styles.bodyText,
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                        }}
                    >
                        {t[lang].thankYouBody}
                    </p>
                </div>
                {renderFooter()}
            </div>
        );
    }

    return (
        <div style={styles.page}>
            {renderHeader()}
            <div style={styles.container}>
                {/* Site header */}
                <div style={styles.formHeaderSection}>
                    {siteInfo?.siteName && (
                        <p style={styles.siteName}>{siteInfo.siteName}</p>
                    )}
                    <p style={styles.title}>{t[lang].title}</p>
                </div>

                {/* Form fields */}
                <div style={styles.formFields}>
                    {/* Category */}
                    <div style={styles.fieldGroup}>
                        <p style={styles.fieldLabel}>
                            {t[lang].categoryLabel}
                            <span style={styles.required}>*</span>
                        </p>
                        <div style={styles.radioList}>
                            {categories.map((cat: any, index: number) => {
                                const isFirst = index === 0;
                                const isLast = index === categories.length - 1;
                                const isSelected =
                                    selectedCategory === String(cat.id);
                                return (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        onClick={() => {
                                            setSelectedCategory(String(cat.id));
                                            setValidationErrors((prev) => ({
                                                ...prev,
                                                category: '',
                                            }));
                                        }}
                                        style={{
                                            ...styles.radioItem,
                                            borderTopLeftRadius: isFirst
                                                ? 10
                                                : 0,
                                            borderTopRightRadius: isFirst
                                                ? 10
                                                : 0,
                                            borderBottomLeftRadius: isLast
                                                ? 10
                                                : 0,
                                            borderBottomRightRadius: isLast
                                                ? 10
                                                : 0,
                                            marginBottom: isLast ? 0 : -1,
                                        }}
                                    >
                                        <div
                                            style={{
                                                ...styles.radioCircle,
                                                backgroundColor: isSelected
                                                    ? '#ff6701'
                                                    : 'transparent',
                                                borderColor: isSelected
                                                    ? '#ff6701'
                                                    : '#c5c7cd',
                                            }}
                                        />
                                        <span style={styles.radioLabel}>
                                            {cat.name}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {validationErrors.category && (
                            <p style={styles.errorText}>
                                {validationErrors.category}
                            </p>
                        )}
                    </div>

                    {/* Description */}
                    <div style={styles.fieldGroup}>
                        <p style={styles.fieldLabel}>
                            {t[lang].descriptionLabel}
                        </p>
                        <textarea
                            placeholder=""
                            value={description}
                            onChange={(e) => {
                                setDescription(e.target.value);
                                setValidationErrors((prev) => ({
                                    ...prev,
                                    description: '',
                                }));
                            }}
                            maxLength={512}
                            style={styles.textarea}
                            rows={4}
                        />
                        {validationErrors.description && (
                            <p style={styles.errorText}>
                                {validationErrors.description}
                            </p>
                        )}
                    </div>

                    {/* Picture */}
                    <div style={styles.fieldGroup}>
                        <p style={styles.fieldLabel}>{t[lang].pictureLabel}</p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{display: 'none'}}
                            onChange={handleImageSelect}
                        />
                        <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            style={{display: 'none'}}
                            onChange={handleImageSelect}
                        />
                        <button
                            type="button"
                            onClick={() =>
                                isAndroid
                                    ? setShowUploadSheet(true)
                                    : fileInputRef.current?.click()
                            }
                            style={{
                                ...styles.uploadButton,
                                opacity: images.length >= 10 ? 0.5 : 1,
                            }}
                            disabled={images.length >= 10}
                        >
                            <CameraIcon />
                            {t[lang].uploadPhotos}
                        </button>

                        {images.length > 0 && (
                            <div style={styles.imageGrid}>
                                {images.map((img) => (
                                    <div
                                        key={img.preview}
                                        style={styles.imageThumbWrap}
                                    >
                                        <img
                                            src={img.preview}
                                            alt=""
                                            style={styles.imageThumb}
                                        />
                                        {img.uploading && (
                                            <div style={styles.imageOverlay}>
                                                <PuffLoader
                                                    color={'#fff'}
                                                    loading={true}
                                                    size={32}
                                                />
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                removeImage(img.preview)
                                            }
                                            style={styles.removeImageBtn}
                                            aria-label="Remove image"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Divider + Send area */}
            <div style={styles.sendSection}>
                <div style={styles.divider} />
                <div style={styles.sendRow}>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={
                            submitting || images.some((img) => img.uploading)
                        }
                        style={{
                            ...styles.sendButton,
                            opacity:
                                submitting ||
                                images.some((img) => img.uploading)
                                    ? 0.6
                                    : 1,
                        }}
                    >
                        {submitting ? (
                            <PuffLoader
                                color={'#fff'}
                                loading={true}
                                size={24}
                            />
                        ) : (
                            t[lang].send
                        )}
                    </button>
                </div>
                {rateLimitMessage && (
                    <p style={styles.rateLimitText}>{rateLimitMessage}</p>
                )}
            </div>

            {renderFooter()}

            {/* Android upload choice sheet */}
            {showUploadSheet && (
                <div
                    style={styles.sheetOverlay}
                    onClick={() => setShowUploadSheet(false)}
                >
                    <div
                        style={styles.sheet}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            style={styles.sheetOption}
                            onClick={() => {
                                setShowUploadSheet(false);
                                cameraInputRef.current?.click();
                            }}
                        >
                            <CameraIcon2 />
                            {t[lang].takePhoto}
                        </button>
                        <div style={styles.sheetDivider} />
                        <button
                            type="button"
                            style={styles.sheetOption}
                            onClick={() => {
                                setShowUploadSheet(false);
                                fileInputRef.current?.click();
                            }}
                        >
                            <GalleryIcon />
                            {t[lang].uploadPhotos}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    page: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        fontFamily: 'Figtree, Arial, sans-serif',
    },
    header: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 20,
        paddingBottom: 20,
        backgroundColor: '#ffffff',
    },
    logo: {
        height: 40,
        width: 'auto',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        maxWidth: 600,
        margin: '0 auto',
        padding: '0 20px',
        boxSizing: 'border-box' as const,
    },
    notFoundContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        paddingTop: 32,
    },
    loaderWrap: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 60,
    },
    formHeaderSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 32,
    },
    siteName: {
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 15,
        fontWeight: 500,
        color: '#5c6266',
        margin: 0,
        marginBottom: 8,
        lineHeight: '20px',
    },
    title: {
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 24,
        fontWeight: 500,
        color: '#36393c',
        margin: 0,
        lineHeight: '32px',
    },
    bodyText: {
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 17,
        fontWeight: 400,
        color: '#36393c',
        margin: 0,
        marginTop: 4,
        lineHeight: '22px',
        whiteSpace: 'pre-line',
    },
    formFields: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    fieldLabel: {
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 15,
        fontWeight: 500,
        color: '#5c6266',
        margin: 0,
        lineHeight: '20px',
    },
    required: {
        color: '#db1a24',
    },
    radioList: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    radioItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px',
        border: '1px solid #dfe1e6',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'left' as const,
        minHeight: 56,
        boxSizing: 'border-box' as const,
    },
    radioCircle: {
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: '1.5px solid #c5c7cd',
        flexShrink: 0,
        boxSizing: 'border-box' as const,
    },
    radioLabel: {
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 17,
        fontWeight: 400,
        color: '#36393c',
        lineHeight: '24px',
    },
    textarea: {
        width: '100%',
        padding: '12px',
        fontSize: 17,
        fontFamily: 'Figtree, Arial, sans-serif',
        fontWeight: 400,
        color: '#36393c',
        border: '1px solid #dfe1e6',
        borderRadius: 10,
        backgroundColor: '#ffffff',
        resize: 'vertical',
        boxSizing: 'border-box' as const,
        lineHeight: '24px',
        outline: 'none',
        minHeight: 100,
    },
    uploadButton: {
        width: '100%',
        height: 56,
        padding: '0 16px',
        fontSize: 17,
        fontFamily: 'Figtree, Arial, sans-serif',
        fontWeight: 500,
        color: '#ffffff',
        backgroundColor: '#ff6701',
        border: 'none',
        borderRadius: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 11,
        boxSizing: 'border-box' as const,
        lineHeight: '22px',
    },
    imageGrid: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 10,
        marginTop: 6,
    },
    imageThumbWrap: {
        position: 'relative' as const,
        width: 'calc(50% - 5px)',
        aspectRatio: '1',
        borderRadius: 10,
        overflow: 'hidden',
    },
    imageThumb: {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
        display: 'block',
    },
    imageOverlay: {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    removeImageBtn: {
        position: 'absolute' as const,
        top: 6,
        right: 6,
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: 'rgba(0,0,0,0.65)',
        color: '#fff',
        border: 'none',
        fontSize: 18,
        lineHeight: '28px',
        textAlign: 'center' as const,
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    sendSection: {
        width: '100%',
        maxWidth: 600,
        margin: '24px auto 0',
        padding: '0 20px',
        boxSizing: 'border-box' as const,
    },
    divider: {
        width: '100%',
        height: 1,
        backgroundColor: '#dfe1e6',
        marginBottom: 16,
    },
    sendRow: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    sendButton: {
        width: 104,
        height: 56,
        padding: '12px 32px',
        fontSize: 17,
        fontFamily: 'Figtree, Arial, sans-serif',
        fontWeight: 500,
        color: '#fff',
        backgroundColor: '#ff6701',
        border: 'none',
        borderRadius: 10,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box' as const,
        lineHeight: '24px',
    },
    errorText: {
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 15,
        fontStyle: 'italic',
        color: '#db1a24',
        margin: 0,
        marginTop: 4,
    },
    rateLimitText: {
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 15,
        fontStyle: 'italic',
        color: '#db1a24',
        margin: 0,
        marginTop: 12,
        textAlign: 'center' as const,
        lineHeight: '20px',
    },
    sheetOverlay: {
        position: 'fixed' as const,
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-end',
        zIndex: 1000,
    },
    sheet: {
        width: '100%',
        backgroundColor: '#ffffff',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        padding: '8px 0 32px',
    },
    sheetOption: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        width: '100%',
        padding: '16px 24px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 17,
        fontWeight: 500,
        color: '#36393c',
        textAlign: 'left' as const,
    },
    sheetDivider: {
        height: 1,
        backgroundColor: '#dfe1e6',
        margin: '0 24px',
    },
    footer: {
        display: 'flex',
        justifyContent: 'center',
        padding: '20px 16px',
        marginTop: 16,
    },
    footerText: {
        fontFamily: 'Figtree, Arial, sans-serif',
        fontSize: 15,
        fontWeight: 500,
        color: '#5c6266',
    },
};

export default ServiceReport;
